import React, { useState, useEffect } from "react";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import { MdCancel } from "react-icons/md";
import buttonCircle from "../images/buttoncircle.png";
import "./connectForm.css";
import { db } from "../firebase"; // Make sure firebase.js is configured
import { collection, addDoc, Timestamp } from "firebase/firestore";
import emailjs from "emailjs-com";
import { addEnquiry } from "../services/api";

export default function ConnectForm({ selectedBranch }) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "Branch name",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Update form location when selectedBranch prop changes
  useEffect(() => {
    if (selectedBranch) {
      setFormData(prev => ({
        ...prev,
        location: selectedBranch
      }));
    }
  }, [selectedBranch]);

  // Toast notification function
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000); // Toast disappears after 4 seconds
  };

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return "Full name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters long";
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      return "Name should only contain letters and spaces";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return "Phone number is required";
    }
    if (!/^\d{10}$/.test(phone.trim())) {
      return "Phone number must be exactly 10 digits";
    }
    return "";
  };

  const validateLocation = (location) => {
    if (!location || location === "Branch name") {
      return "Please select a preferred branch";
    }
    return "";
  };

  const validateForm = () => {
    const newErrors = {
      fullName: validateName(formData.fullName),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      location: validateLocation(formData.location),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone number - only allow digits and limit to 10
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, ""); // Remove non-digits
      if (numericValue.length <= 10) {
        setFormData((prev) => ({
          ...prev,
          [name]: numericValue,
        }));
        // Clear phone error when user starts typing valid input
        if (errors.phone) {
          setErrors((prev) => ({ ...prev, phone: "" }));
        }
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Check internet connectivity first
      if (!navigator.onLine) {
        throw new Error('NO_INTERNET');
      }

      // Save to Firestore
      await addDoc(collection(db, "contacts"), {
        ...formData,
        createdAt: Timestamp.now(),
      });

      // Send to Backend API
      await addEnquiry(formData);

      // Send email using EmailJS
      await emailjs.send(
        "service_n5ei8sj",      // Replace with your EmailJS service ID
        "template_qujozy3",     // Replace with your EmailJS template ID
        {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
        },
        "sYyssIlC6KDEHlg-7"        // Replace with your EmailJS public key
      );

      showToast("Message sent successfully! We'll get back to you soon.", 'success');
      setFormData({ fullName: "", email: "", phone: "", location: "Branch name" });
      setErrors({}); // Clear all errors
    } catch (error) {
      console.error("❌ Error:", error);

      // Handle specific error types
      if (error.message === 'NO_INTERNET' || !navigator.onLine) {
        showToast("No internet connection. Please check your network and try again.", 'network');
      } else if (error.code === 'unavailable' || error.message.includes('network')) {
        showToast("Network error occurred. Please check your connection and try again.", 'network');
      } else if (error.code === 'permission-denied') {
        showToast("Permission denied. Please try again later.", 'error');
      } else if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        showToast("Connection failed. Please check your internet connection.", 'network');
      } else {
        showToast("Something went wrong. Please try again later.", 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="connectForm" className="pageWrapper">
      <div className="connectContainer">
        <h2 className="heading">
          Let's <span>Connect</span>
        </h2>
        <p className="subheading">
          Ready to level up? Drop us a message and we'll help you get started.
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <label className="label">
            Full Name
            <input
              type="text"
              name="fullName"
              placeholder="Jeshy Doe"
              className={`input ${errors.fullName ? 'input-error' : ''}`}
              value={formData.fullName}
              onChange={handleChange}
              required
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </label>

          <label className="label">
            Email
            <input
              type="email"
              name="email"
              placeholder="your@gmail.com"
              className={`input ${errors.email ? 'input-error' : ''}`}
              value={formData.email}
              onChange={handleChange}
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </label>

          <div className="row">
            <label className="rowLabel">
              Phone Number
              <input
                type="text"
                name="phone"
                placeholder="1234567890"
                className={`input ${errors.phone ? 'input-error' : ''}`}
                value={formData.phone}
                onChange={handleChange}
                maxLength="10"
                required
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </label>

            <label className="rowLabel">
              Preferred Location
              <div className="selectWrapper">
                <select
                  name="location"
                  className={`select ${errors.location ? 'input-error' : ''}`}
                  value={formData.location}
                  onChange={handleChange}
                  required
                >
                  <option value="Branch name" disabled>Branch name</option>
                  <option value="Stonehousepet, Nellore">Stonehousepet, Nellore</option>
                  <option value="Harinathpuram, Nellore">Harinathpuram, Nellore</option>
                  <option value="Vanamthopu Center, Nellore">Vanamthopu Center, Nellore</option>
                  <option value="Current Office Center, Nellore">Current Office Center, Nellore</option>
                  <option value="Vedayapalem, Nellore">Vedayapalem, Nellore</option>
                  <option value="BV Nagar, Nellore">BV Nagar, Nellore</option>
                  <option value="Dhanalakshmi Puram, Nellore">Dhanalakshmi Puram, Nellore</option>
                </select>
                <IoIosArrowDropdownCircle className="selectIcon" />
              </div>
              {errors.location && <span className="error-message">{errors.location}</span>}
            </label>
          </div>

          <button className="customButton" type="submit" disabled={loading}>
            <span className="buttonText">
              {loading ? "Sending..." : "Send Message"}
            </span>
            <span className="buttonIcon">
              <img src={buttonCircle} alt="Button Circle" />
            </span>
          </button>
        </form>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'success' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#10b981" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                </svg>
              ) : toast.type === 'network' ? (
                <MdCancel size={20} color="#ef4444" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-exclamation-circle" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                  <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                </svg>
              )}
            </div>
            <div className="toast-text">
              <div className="toast-title">
                {toast.type === 'success' ? 'Success!' :
                  toast.type === 'network' ? 'Connection Error!' : 'Error!'}
              </div>
              <div className="toast-message">{toast.message}</div>
            </div>
          </div>
          <button
            className="toast-close"
            onClick={() => setToast(null)}
            aria-label="Close notification"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-x" viewBox="0 0 16 16">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
